/**
 * FudswaccaPanel — Analyse Fudswacca de l'AUTRE (V23.14.8).
 *
 * Sous-mode du mode FRED (mère) : analyse Stratégie · Tactique · Opérationnel
 * d'une entité pour déterminer si on peut lui rendre service.
 *
 * Pattern async-job (Cloudflare-safe).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Eye, Loader2, Sparkles, RefreshCw, AlertTriangle, CheckCircle2, XCircle, HelpCircle,
  Target, Crosshair, Wrench, Handshake, ChevronDown, ChevronUp,
} from "lucide-react";
import FreshnessBadge from "./FreshnessBadge";

const VERDICT_META = {
  OUI: { color: "#1D9E75", Icon: CheckCircle2, label: "OUI · service possible" },
  "PEUT-ETRE": { color: "#EF9F27", Icon: HelpCircle, label: "PEUT-ÊTRE · à creuser" },
  NON: { color: "#E24B4A", Icon: XCircle, label: "NON · pas d'angle pour l'instant" },
};

export default function FudswaccaPanel({ entityId, entityName }) {
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState("");
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  useEffect(() => {
    if (!entityId) return undefined;
    let cancelled = false;
    setData(null); setPhase("idle"); setError(null);
    (async () => {
      try {
        const r = await api.get(`/workspace/fudswacca/entities/${entityId}/latest`);
        if (cancelled) return;
        if (r.data?.available) { setData(r.data); setPhase("done"); }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; stopPolling(); };
  }, [entityId]);

  const generate = useCallback(async () => {
    setPhase("running"); setError(null); setProgress("Démarrage…");
    try {
      const r = await api.post("/workspace/fudswacca/analyze", {
        entity_id: entityId, entity_name: entityName,
      });
      const job_id = r.data?.job_id;
      if (!job_id) throw new Error("Pas de job_id");
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get(`/workspace/fudswacca/jobs/${job_id}`);
          const d = s.data || {};
          setProgress(d.progress || "");
          if (d.status === "done") {
            stopPolling();
            const latest = await api.get(`/workspace/fudswacca/entities/${entityId}/latest`);
            if (latest.data?.available) setData(latest.data);
            setPhase("done");
            notify.success("Analyse Fudswacca générée.");
          } else if (d.status === "failed") {
            stopPolling(); setError(d.error || "Erreur"); setPhase("failed");
          }
        } catch (e) { stopPolling(); setError(fmtError(e)); setPhase("failed"); }
      }, 2000);
    } catch (e) { setError(fmtError(e)); setPhase("failed"); }
  }, [entityId, entityName]);

  const verdict = data?.service_possible?.verdict;
  const vMeta = VERDICT_META[verdict] || null;
  const VIcon = vMeta?.Icon;

  return (
    <div className="border border-[#7F77DD]/30 bg-gradient-to-br from-[#7F77DD]/[0.06] to-transparent rounded-md p-3 space-y-3" data-testid="fudswacca-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Eye className="w-4 h-4 text-[#7F77DD]" />
          <span className="text-sm font-medium text-zinc-100">Analyse Fudswacca</span>
          <span className="text-[10px] uppercase tracking-wider text-[#7F77DD]">Mode FRED · sous-mode</span>
          {data && (
            <FreshnessBadge
              lastGeneratedAt={data.created_at}
              moduleLabel={`Fudswacca · ${entityName || ""}`}
              testid="fudswacca-freshness"
            />
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={generate}
            disabled={phase === "running"}
            className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="fudswacca-generate-btn"
          >
            {phase === "running" ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> {progress || "Analyse…"}</>
            ) : data ? (
              <><RefreshCw className="w-3 h-3" /> Régénérer</>
            ) : (
              <><Sparkles className="w-3 h-3" /> Lancer Fudswacca</>
            )}
          </button>
          {data && (
            <button onClick={() => setExpanded((v) => !v)} className="text-zinc-500 hover:text-zinc-200 p-1" data-testid="fudswacca-toggle">
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </div>

      {phase === "idle" && !data && (
        <p className="text-[11px] text-zinc-400 italic">
          Noam décortique la <b>Stratégie</b>, <b>Tactique</b> et <b>Opérationnel</b> de l&apos;acteur pour déterminer <b>si vous pouvez lui rendre service</b>.
        </p>
      )}

      {phase === "failed" && error && (
        <div className="p-2 rounded border border-[#E24B4A]/40 bg-[#E24B4A]/10 text-[11px] text-[#E24B4A]" data-testid="fudswacca-error">
          <AlertTriangle className="w-3 h-3 inline mr-1" /> {error}
        </div>
      )}

      {phase === "done" && data && expanded && (
        <div className="space-y-3" data-testid="fudswacca-results">
          {/* Verdict */}
          {vMeta && (
            <div className="p-2 rounded border" style={{ borderColor: `${vMeta.color}55`, background: `${vMeta.color}14` }}>
              <div className="flex items-center gap-2 text-sm font-medium" style={{ color: vMeta.color }} data-testid="fudswacca-verdict">
                <VIcon className="w-4 h-4" /> {vMeta.label}
              </div>
              {data.service_possible?.verdict_reason && (
                <div className="text-[11px] text-zinc-300 mt-1">{data.service_possible.verdict_reason}</div>
              )}
            </div>
          )}

          {/* S/T/O grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            <Block icon={Target} color="#378ADD" title="Stratégique" data-testid="fudswacca-strategique">
              {data.strategique?.vision_probable && <div className="text-[11px] text-zinc-300 mb-1.5">{data.strategique.vision_probable}</div>}
              <Tags label="Moteurs" items={data.strategique?.moteurs} color="#1D9E75" />
              <Tags label="Contraintes" items={data.strategique?.contraintes} color="#EF9F27" />
            </Block>
            <Block icon={Crosshair} color="#7F77DD" title="Tactique" data-testid="fudswacca-tactique">
              {data.tactique?.modele_org && <div className="text-[11px] text-zinc-300 mb-1.5">{data.tactique.modele_org}</div>}
              <Tags label="Leviers" items={data.tactique?.leviers} color="#378ADD" />
              {data.tactique?.rythme && <div className="text-[10px] text-zinc-500 italic mt-1">Rythme : {data.tactique.rythme}</div>}
            </Block>
            <Block icon={Wrench} color="#EFD127" title="Opérationnel" data-testid="fudswacca-operationnel">
              <Tags label="Activités clés" items={data.operationnel?.activites_cles} color="#1D9E75" />
              <Tags label="Frictions probables" items={data.operationnel?.points_friction_probables} color="#E24B4A" />
              <Tags label="Interlocuteurs pivots" items={data.operationnel?.interlocuteurs_pivots} color="#378ADD" />
            </Block>
          </div>

          {/* Offres de service */}
          {data.service_possible?.service_offers?.length > 0 && (
            <div className="border border-[#1D9E75]/30 bg-[#1D9E75]/[0.04] rounded p-2">
              <div className="noam-overline mb-1.5 flex items-center gap-1.5 text-[#1D9E75]">
                <Handshake className="w-3 h-3" /> Offres de service que vous pouvez proposer
              </div>
              <ul className="space-y-1.5" data-testid="fudswacca-offers">
                {data.service_possible.service_offers.map((o, i) => (
                  <li key={i} className="text-[11px]" data-testid={`fudswacca-offer-${i}`}>
                    <div className="text-zinc-100 font-medium">{o.title || `Offre ${i+1}`}</div>
                    <div className="text-zinc-400">{o.angle || o.description || ""}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.ethical_notes && (
            <div className="text-[10px] text-zinc-500 italic border-l-2 border-zinc-700 pl-2">
              Note d&apos;éthique : {data.ethical_notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Block({ icon: Icon, color, title, children, ...rest }) {
  return (
    <div className="rounded border border-white/10 bg-white/[0.02] p-2" {...rest}>
      <div className="noam-overline mb-1.5 flex items-center gap-1" style={{ color }}>
        <Icon className="w-3 h-3" /> {title}
      </div>
      {children}
    </div>
  );
}

function Tags({ label, items, color }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-1.5">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((it, i) => (
          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${color}1A`, color, border: `1px solid ${color}33` }}>
            {typeof it === "string" ? it : (it.title || it.label || JSON.stringify(it))}
          </span>
        ))}
      </div>
    </div>
  );
}
